import { IonSpinner } from '@ionic/react';
import { useTranslation, Trans } from 'react-i18next';
import { VerificationCode } from './VerificationCode';
import type { OrderPartySummary } from '../../types/order';

interface VerificationBubblesProps {
  /** Real handoff code the buyer confirms with the lister before pressing confirm. */
  code: number;
  onSelect: (code: number) => void;
  isSubmitting?: boolean;
  /** Lister whose name appears in the instructional copy. */
  lister?: Pick<OrderPartySummary, 'businessName' | 'displayName'>;
  /**
   * The orderer has already confirmed their half of the handshake but the lister
   * hasn't completed it yet — show the "waiting on the chef" state instead of the
   * interactive prompt (the code stays visible, but the button is disabled).
   */
  confirmed?: boolean;
  /**
   * The LISTER has already pressed Handed Off and the orderer hasn't confirmed
   * yet. The button stays live (their press is the only step left), but the
   * copy drops the "confirm this number with your chef" ritual — the chef has
   * already run it and moved on, so instructing the orderer to wait on them
   * reads as wrong. Ignored once `confirmed` is set; that state is strictly later.
   */
  handedOff?: boolean;
}

/**
 * The buyer reads the number aloud to confirm the chef has the right order,
 * then presses "I got my order" to complete. The number is display-only; the
 * confirm is a plain rectangle matching the rest of the order page's buttons.
 * When both parties have confirmed the order completes and payment captures;
 * if the orderer presses first, the button flips to the disabled
 * `confirmed` "waiting on the chef" state until the lister finishes. If the
 * LISTER goes first, `handedOff` swaps the read-aloud copy for "your chef marked
 * this handed off" — the button is unchanged, only the framing.
 */
export const VerificationBubbles: React.FC<VerificationBubblesProps> = ({
  code,
  onSelect,
  isSubmitting = false,
  lister,
  confirmed = false,
  handedOff = false,
}) => {
  const { t } = useTranslation('orders');

  const handleConfirm = () => {
    if (isSubmitting || confirmed) return;
    onSelect(code);
  };

  // Two keys instead of one with a fallback interpolation: Spanish's
  // `de el` → `del` contraction means the no-name fallback can't be
  // dropped into the same template the named variant uses.
  //
  // Named variant uses <Trans> so the {{name}} interpolation can be wrapped
  // in a styled <span> (brand-primary, bold) without splitting the sentence
  // — `<name>` is the i18next tag that maps to the `name` slot in
  // `components` below.
  const name = lister?.businessName ?? lister?.displayName ?? '';

  return (
    <div className="space-y-3">
      <p className="whitespace-pre-line text-sm font-medium">
        {confirmed ? (
          name ? (
            <Trans
              i18nKey="orders:verify.waitingOnChef"
              values={{ name }}
              components={{
                name: <span className="font-semibold text-brand-primary" />,
              }}
            />
          ) : (
            t('verify.waitingOnChefNoName')
          )
        ) : handedOff ? (
          name ? (
            <Trans
              i18nKey="orders:verify.handedOffInstructions"
              values={{ name }}
              components={{
                name: <span className="font-semibold text-brand-primary" />,
              }}
            />
          ) : (
            t('verify.handedOffInstructionsNoName')
          )
        ) : name ? (
          <Trans
            i18nKey="orders:verify.confirmInstructions"
            values={{ name }}
            components={{
              name: <span className="font-semibold text-brand-primary" />,
            }}
          />
        ) : (
          t('verify.confirmInstructionsNoName')
        )}
      </p>
      <VerificationCode code={code} confirmed={confirmed} />
      <button
        type="button"
        onClick={handleConfirm}
        disabled={confirmed || isSubmitting}
        className="flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-brand-primary py-2 text-sm font-semibold text-white active:opacity-80 disabled:opacity-50"
      >
        {isSubmitting && !confirmed ? (
          <IonSpinner name="crescent" className="h-4 w-4" />
        ) : confirmed ? (
          t('verify.waitingCta')
        ) : (
          t('verify.gotItCta')
        )}
      </button>
    </div>
  );
};
